import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  InputBase,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close,
  ExpandLess,
  ExpandMore,
  ChevronRight,
  Dashboard as DashboardIcon,
  Star as StarIcon,
  AccountCircle,
  Logout,
  People,
  Settings,
  LocationCity,
  ViewList,
  Apps,
  Home,
  BarChart,
  PieChart,
  Assessment,
  Analytics as AnalyticsIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Folder,
  Description,
  Language,
  Image,
  VideoLibrary,
  Map,
  LocationOn,
  Email,
  CalendarToday,
  DirectionsWalk,
  GroupRemove,
  EditNote,
  PersonSearch,
  Schedule,
  Star,
  Favorite,
  Bookmark,
  ShoppingCart,
  Store,
  Business,
  School,
  LocalHospital,
  Security,
  Lock,
  Public,
  Cloud,
  Storage,
  Computer,
  Phone,
  Tablet,
  List as ListIcon,
  GridView,
  Group,
  Person,
  AdminPanelSettings,
  Build,
  Engineering,
  Science,
  Sports,
  MusicNote,
  Movie,
  Games,
  Restaurant,
  Flight,
  DirectionsCar,
  Train,
  DirectionsBus,
  Search,
  SwapHoriz,
  EmojiEvents as EmojiEventsIcon,
  AttachMoney as AttachMoneyIcon,
  FactCheck as FactCheckIcon,
  Hub as HubIcon,
  Warning as WarningIcon,
  Vaccines as VaccinesIcon,
  Shield as ShieldIcon,
  People as PeopleIcon,
  MonitorHeart as MonitorHeartIcon,
  Hotel as HotelIcon,
  MedicalServices as MedicalServicesIcon,
  Timer as TimerIcon,
  Biotech as BiotechIcon,
  Medication as MedicationIcon,
  LocalShipping,
  Psychology,
  AccountTree,
  TrendingUp,
  Coronavirus as CoronavirusIcon,
  AccessTime as AccessTimeIcon,
  HourglassBottom as HourglassBottomIcon,
  MeetingRoom as MeetingRoomIcon,
  ReceiptLong as ReceiptLongIcon,
  KingBed as KingBedIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Paid as PaidIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/ContextProvider';
import { useBranding } from '../contexts/BrandingContext';
import { municipiosService } from '../services/api';
import AlertaBadge from './AlertaBadge';
import UnidadeSaudeSelector from './UnidadeSaudeSelector';
import { useUnidade } from '../contexts/UnidadeContext';
import { crescer } from '../theme';

const SIDEBAR_WIDTH = 256;

// Cores da sidebar (Slate-800 / educacao_v2 style)
const SB = {
  bg: crescer.sbBg,
  grad: crescer.sbGrad,
  hover: crescer.sbHover,
  active: crescer.sbActive,
  text: crescer.sbText,
  textMuted: crescer.sbMuted,
  accent: crescer.sbAccent,
  border: crescer.sbBorder,
};

// Itens do menu lateral no padrão Crescer: ativo com gradiente de marca + trilho à esquerda.
// Só visual — comportamento (onClick, permissões) continua em cada item.
const navItemSx = (isActive: boolean) => ({
  position: 'relative',
  borderRadius: '10px',
  py: 1,
  px: 1.5,
  gap: 1.5,
  color: isActive ? 'white' : '#cbd5e1',
  background: isActive ? 'linear-gradient(90deg, rgba(0,122,240,.22), rgba(109,74,224,.16))' : 'transparent',
  boxShadow: isActive ? 'inset 0 0 0 1px rgba(96,165,250,.25)' : 'none',
  '& .MuiListItemIcon-root': { color: isActive ? '#7dd3fc' : '#8b9bb4', transition: 'color .18s ease' },
  '&:hover': {
    background: isActive ? 'linear-gradient(90deg, rgba(0,122,240,.28), rgba(109,74,224,.2))' : SB.hover,
    color: 'white',
    transform: isActive ? 'none' : 'translateX(3px)',
  },
  '&:hover .MuiListItemIcon-root': { color: isActive ? '#7dd3fc' : '#cbd5e1' },
  '&::before': isActive
    ? {
        content: '""',
        position: 'absolute',
        left: -12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 4,
        height: 22,
        borderRadius: '0 4px 4px 0',
        background: 'linear-gradient(180deg, #38bdf8, #6d4ae0)',
        boxShadow: '0 0 10px rgba(56,189,248,.7)',
      }
    : {},
  '&.Mui-focusVisible': { outline: `2px solid ${SB.accent}`, outlineOffset: -2 },
  transition: 'background-color .18s ease, color .18s ease, transform .18s ease',
});

const navSectionSx = {
  px: 1.5,
  mb: 0.875,
  fontSize: '0.625rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  color: '#64748b',
} as const;

// Mapeamento de ícones
const iconMap: { [key: string]: React.ComponentType<any> } = {
  dashboard: DashboardIcon, home: Home, settings: Settings, people: People,
  barChart: BarChart, pieChart: PieChart, assessment: Assessment, folder: Folder,
  description: Description, image: Image, videoLibrary: VideoLibrary, map: Map,
  locationOn: LocationOn, email: Email, calendarToday: CalendarToday, schedule: Schedule,
  star: Star, favorite: Favorite, bookmark: Bookmark, shoppingCart: ShoppingCart,
  store: Store, business: Business, school: School, localHospital: LocalHospital,
  security: Security, lock: Lock, public: Public, cloud: Cloud, storage: Storage,
  computer: Computer, phone: Phone, tablet: Tablet, menu: MenuIcon, list: ListIcon,
  gridView: GridView, viewList: ViewList, accountCircle: AccountCircle, group: Group,
  person: Person, adminPanelSettings: AdminPanelSettings, build: Build,
  engineering: Engineering, science: Science, sports: Sports, musicNote: MusicNote,
  movie: Movie, games: Games, restaurant: Restaurant, flight: Flight,
  directionsCar: DirectionsCar, train: Train, directionsBus: DirectionsBus,
};

const getIconComponent = (iconName: string | undefined): React.ComponentType<any> => {
  if (!iconName) return DashboardIcon;
  return iconMap[iconName] || DashboardIcon;
};

// Mapeamento de segmentos de rota para labels amigaveis no breadcrumb
const segmentLabels: Record<string, string> = {
  // Admin
  'usuarios': 'Usuarios',
  'menus': 'Menus',
  'municipios': 'Municipios',
  'novo': 'Novo município',
  'perfis': 'Perfis',
  'configuracoes': 'Configuracoes',
  // Saúde 360°
  'saude': 'Saúde 360°',
  'ranking': 'Ranking de Equipes',
  'financeiro': 'Simulador Financeiro',
  'qualidade-dados': 'Qualidade de Dados',
  'dados-sociodemograficos': 'Perfil Demografico',
  'cobertura-vacinal': 'Cobertura Vacinal',
  'atendimentos-24h': 'Atendimentos 24h',
  'filas-espera': 'Filas e Espera',
  'filas-consultorios': 'Filas de Consultorios',
  'indice-prescricoes': 'Indice de Prescricoes',
  'ocupacao-leitos': 'Ocupacao de Leitos',
  'lista': 'Lista Nominal',
  // Eixos eSF/eAP
  'c1-mais-acesso': 'C1 - Mais Acesso',
  'c2-criancas': 'C2 - Saude da Crianca',
  'c3-gestantes': 'C3 - Saude da Gestante',
  'c4-diabeticos': 'C4 - Diabetes',
  'c5-hipertensos': 'C5 - Hipertensao',
  'c6-idosos': 'C6 - Saude do Idoso',
  'c7-mulheres': 'C7 - Saude da Mulher',
  // Odonto (eSB)
  'odonto': 'Saude Bucal',
  'b1-primeira-consulta': 'B1 - 1a Consulta',
  'b2-tratamento-concluido': 'B2 - Tratamento Concluido',
  'b3-exodontia': 'B3 - Exodontia',
  'b4-escovacao': 'B4 - Escovacao',
  'b5-preventivos': 'B5 - Preventivos',
  'b6-tra-art': 'B6 - TRA/ART',
  // Complementares
  'producao': 'Produção Geral',
  'especializada': 'Atenção Especializada',
  'rede': 'Rede Especializada',
  'prospeccao': 'Prospecção',
  'predicao': 'Predição IA',
  'planejamento': 'Planejamento',
  'custos': 'Custos',
  'nt30-vinculo': 'Vinculo Territorial',
  'cvat': 'Vinculo e Acompanhamento Territorial',
  // eMulti
  'emulti': 'eMulti',
  'm1-media-atendimentos': 'M1 - Media de Atendimentos',
  'm2-acoes-interprofissionais': 'M2 - Acoes Interprofissionais',
  // eAPP
  'eapp-acesso': 'eAPP Acesso',
  'eapp-gestacao': 'eAPP Gestacao',
  'eapp-diabetes-has': 'eAPP Diabetes/HAS',
  'eapp-ist': 'eAPP IST',
  'eapp-cancer': 'eAPP Cancer',
  'eapp-tuberculose': 'Tuberculose (Prisional)',
  // eCR
  'ecr-acesso': 'eCR Acesso',
  'ecr-gestacao': 'eCR Gestacao',
  'ecr-ist': 'eCR IST',
  'ecr-tuberculose': 'eCR Tuberculose',
  'relatorio-pdf': 'Relatorio PDF',
  'relatorios': 'Relatorios',
  'resumo-executivo': 'Resumo Executivo',
  'equipes-sem-producao': 'Equipes sem Producao',
  'fichas-rascunho': 'Fichas em Rascunho',
  'profissionais-duplicados': 'Profissionais Duplicados',
  'painel-ms': 'Painel Ministerio da Saude',
  // Monitoramento
  'monitoramento': 'Monitoramento',
  'leitos': 'Leitos',
  'internacoes': 'Internacoes',
  'tempos-espera': 'Tempos de Espera',
  'regulacao': 'Regulacao',
  'fila-espera': 'Fila de Espera',
  'dispensacoes': 'Farmacia',
  'cid': 'CID / Diagnosticos',
  'geo': 'Geolocalizacao',
  'pacientes': 'Pacientes',
  'alertas': 'Alertas',
  'unidades-moveis': 'Unidades Moveis',
  'visa-web': 'Monitoramento Web VISA',
  'vigilancia-epidemiologica': 'Vigilancia Epidemiologica',
  'detalhes': 'Documentos',
  'iegm': 'IEGM',
  'isaude': 'I-Saude',
};

const ModernLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { user, municipioAtivo, logout, hasPermission, setMunicipioAtivo } = useAuth();
  const { menus } = useAppContext();
  const { branding } = useBranding();
  const { unidadeSelecionada } = useUnidade();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<{ [key: number]: boolean }>({});
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [municipioAnchor, setMunicipioAnchor] = useState<null | HTMLElement>(null);
  const [userMunicipios, setUserMunicipios] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const isDashboardPage = location.pathname.startsWith('/dashboard/');
  const currentSubmenuId = location.pathname.startsWith('/dashboard/')
    ? parseInt(location.pathname.split('/dashboard/')[1] || '0')
    : null;

  // Expandir menu que contém submenu ativo
  useEffect(() => {
    if (currentSubmenuId) {
      menus.forEach((menu) => {
        if (menu.submenus?.some((s: any) => s.id === currentSubmenuId)) {
          setOpenMenus((prev) => {
            if (prev[menu.id]) return prev; // já aberto, não re-setar
            return { ...prev, [menu.id]: true };
          });
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubmenuId]);

  // Carregar municípios do usuário
  useEffect(() => {
    if (user?.id) {
      municipiosService.getMunicipiosByUser()
        .then(setUserMunicipios)
        .catch(() => {});
    }
  }, [user]);

  // Botão "voltar" do Android (app Capacitor): navega no histórico do React Router
  // em vez de fechar o app; só sai do app quando já está na home.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let remove: (() => void) | undefined;
    CapacitorApp.addListener('backButton', () => {
      if (window.location.pathname !== '/' && window.history.length > 1) {
        navigate(-1);
      } else {
        CapacitorApp.exitApp();
      }
    }).then((handle) => {
      remove = () => handle.remove();
    });
    return () => {
      if (remove) remove();
    };
  }, [navigate]);

  // Filtrar menus pela busca
  const filteredMenus = useMemo(() => {
    if (!searchTerm.trim()) return menus;
    const term = searchTerm.toLowerCase();
    return menus
      .map((menu) => ({
        ...menu,
        submenus: menu.submenus?.filter((s: any) => s.nome.toLowerCase().includes(term)) || [],
      }))
      .filter((menu) => menu.nome.toLowerCase().includes(term) || menu.submenus.length > 0);
  }, [menus, searchTerm]);

  const handleMenuClick = (menuId: number) => {
    setOpenMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  // Abre o editor BI embarcado (iframe) numa rota interna, mantendo a barra lateral.
  const handleOpenBiEditor = () => {
    if (!municipioAtivo?.id) {
      window.alert('Selecione um municipio ativo para abrir o editor BI.');
      return;
    }
    navigate('/bi/editor');
  };

  const handleSubmenuClick = (submenuId: number) => {
    navigate(`/dashboard/${submenuId}`);
    setMobileOpen(false);
  };

  const handleSelectMunicipio = (municipio: any) => {
    setMunicipioAtivo(municipio);
    setMunicipioAnchor(null);
    // Redireciona para home se estiver num dashboard que pode não existir no novo município
    if (currentSubmenuId) {
      navigate('/');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ─── SIDEBAR CONTENT ───
  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: SB.bg, background: SB.grad, overflow: 'hidden' }}>
      {/* Logo */}
      <Box
        sx={{
          height: 64,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 3,
          borderBottom: `1px solid ${SB.border}`,
          gap: 1.5,
        }}
      >
        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            flex: 1,
          }}
        >
          {branding.branding_logo ? (
            <Box
              component="img"
              src={branding.branding_logo}
              alt={branding.branding_nome}
              sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: '11px', flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: '11px',
                background: crescer.aiGrad,
                boxShadow: '0 4px 14px rgba(0,122,240,.4), inset 0 1px 0 rgba(255,255,255,.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Apps sx={{ fontSize: 20, color: 'white' }} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, color: 'white', letterSpacing: '-0.02em', lineHeight: 1.1 }} noWrap>
              {branding.branding_nome || 'NeoGestor'}
            </Typography>
            {branding.branding_subtitulo && (
              <Typography sx={{ fontSize: '0.625rem', color: SB.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.25 }} noWrap>
                {branding.branding_subtitulo}
              </Typography>
            )}
          </Box>
        </Box>
        {isMobile && (
          <IconButton onClick={() => setMobileOpen(false)} sx={{ color: SB.textMuted, ml: 'auto' }}>
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Município Ativo */}
      {municipioAtivo && (
        <Box
          onClick={(e) => userMunicipios.length > 1 ? setMunicipioAnchor(e.currentTarget) : undefined}
          sx={{
            mx: 1.5,
            mt: 1.5,
            mb: 0.5,
            px: 1.5,
            py: 1,
            borderRadius: '8px',
            backgroundColor: 'rgba(0,122,240,0.12)',
            border: '1px solid rgba(0,122,240,0.25)',
            cursor: userMunicipios.length > 1 ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            transition: 'background-color 0.15s',
            '&:hover': userMunicipios.length > 1 ? { backgroundColor: 'rgba(0,122,240,0.2)' } : {},
          }}
        >
          <LocationCity sx={{ fontSize: 16, color: '#60a5fa' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', lineHeight: 1.2 }}>
              Municipio
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'white', lineHeight: 1.3 }} noWrap>
              {municipioAtivo.nome}
            </Typography>
          </Box>
          {userMunicipios.length > 1 && (
            <SwapHoriz sx={{ fontSize: 16, color: '#60a5fa', flexShrink: 0 }} />
          )}
        </Box>
      )}

      {/* Unidade de Saude (abaixo do Municipio) */}
      <UnidadeSaudeSelector />

      {/* Search */}
      <Box sx={{ px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <Search sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: SB.textMuted }} />
          <InputBase
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,.10)',
              backgroundColor: 'rgba(255,255,255,.05)',
              py: 0.75,
              pl: 4,
              pr: 1.5,
              fontSize: '0.75rem',
              color: SB.text,
              transition: 'border-color .15s ease, box-shadow .15s ease',
              '& input::placeholder': { color: SB.textMuted, opacity: 1 },
              '&:focus-within': { borderColor: crescer.brand, boxShadow: '0 0 0 3px rgba(0,122,240,.25)' },
            }}
          />
        </Box>
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(148,163,184,.35)', borderRadius: 3 },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(148,163,184,.6)' },
        }}
      >
        <List disablePadding sx={{ '& .MuiListItem-root': { mb: 0.25 } }}>
          {filteredMenus.map((menu) => {
            const IconComp = getIconComponent(menu.icone);
            const hasActiveChild = menu.submenus?.some((s: any) => s.id === currentSubmenuId);
            const isOpen = openMenus[menu.id] || (searchTerm.trim().length > 0);

            return (
              <React.Fragment key={menu.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleMenuClick(menu.id)}
                    sx={{
                      ...navItemSx(false),
                      color: hasActiveChild ? 'white' : '#cbd5e1',
                      background: hasActiveChild ? SB.hover : 'transparent',
                      '& .MuiListItemIcon-root': { color: hasActiveChild ? '#7dd3fc' : '#8b9bb4', transition: 'color .18s ease' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                      <IconComp sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={menu.nome}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
                    />
                    {isOpen ? <ExpandLess sx={{ fontSize: 16, color: SB.textMuted }} /> : <ExpandMore sx={{ fontSize: 16, color: SB.textMuted }} />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List
                    disablePadding
                    sx={{
                      ml: 2,
                      mt: 0.5,
                      mb: 0.5,
                      borderLeft: `1px solid ${SB.border}`,
                      pl: 2,
                    }}
                  >
                    {(user?.is_admin || hasPermission('MANAGE_BI')) && /business intelligence|intelig/i.test(menu.nome || '') && (
                      <>
                        <ListItem disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            onClick={handleOpenBiEditor}
                            disabled={!municipioAtivo?.id}
                            sx={{
                              borderRadius: '8px',
                              py: 0.75,
                              px: 1.5,
                              color: SB.textMuted,
                              '&:hover': { backgroundColor: SB.hover, color: 'white' },
                              transition: 'all 0.15s',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                              <BarChart sx={{ fontSize: 14, color: 'inherit' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary="Editor (criar/editar paineis)"
                              primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 600, color: 'inherit' }}
                            />
                          </ListItemButton>
                        </ListItem>
                        <Divider sx={{ my: 0.5, borderColor: SB.border }} />
                      </>
                    )}
                    {menu.submenus?.map((submenu: any) => {
                      const isActive = currentSubmenuId === submenu.id;
                      return (
                        <ListItem key={submenu.id} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            onClick={() => handleSubmenuClick(submenu.id)}
                            sx={{
                              borderRadius: '8px',
                              py: 0.75,
                              px: 1.5,
                              background: isActive ? 'linear-gradient(90deg, rgba(0,122,240,.22), rgba(109,74,224,.16))' : 'transparent',
                              boxShadow: isActive ? 'inset 0 0 0 1px rgba(96,165,250,.25)' : 'none',
                              color: isActive ? 'white' : SB.textMuted,
                              '& .MuiListItemIcon-root': { color: isActive ? '#7dd3fc' : 'inherit' },
                              '&:hover': {
                                background: isActive ? 'linear-gradient(90deg, rgba(0,122,240,.28), rgba(109,74,224,.2))' : SB.hover,
                                color: 'white',
                              },
                              transition: 'all 0.15s',
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                              <StarIcon sx={{ fontSize: 14, color: 'inherit' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={submenu.nome}
                              primaryTypographyProps={{
                                fontSize: '0.8125rem',
                                fontWeight: isActive ? 600 : 400,
                                color: 'inherit',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>

        {/* Saúde 360° Section */}
        {hasPermission('VIEW_SAUDE_INDICADORES') && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Saúde 360°
            </Typography>
            <List disablePadding>
              {[
                { path: '/saude', label: 'Indicadores', icon: LocalHospital },
                { path: '/saude/ranking', label: 'Ranking de Equipes', icon: EmojiEventsIcon },
                { path: '/saude/financeiro', label: 'Simulador Financeiro', icon: AttachMoneyIcon },
                { path: '/saude/painel-ms', label: 'Painel MS (Oficial)', icon: Business },
                { path: '/saude/cobertura-vacinal', label: 'Cobertura Vacinal', icon: VaccinesIcon },
                { path: '/saude/cvat', label: 'Vinculo Territorial', icon: HubIcon },
                { path: '/saude/qualidade-dados', label: 'Qualidade de Dados', icon: FactCheckIcon },
                { path: '/saude/dados-sociodemograficos', label: 'Perfil Demografico', icon: PeopleIcon },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {/* Relatorios Section */}
        {hasPermission('VIEW_SAUDE_LISTA_NOMINAL') && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Relatórios
            </Typography>
            <List disablePadding>
              {[
                { path: '/saude/relatorios/agenda-dia', label: 'Agenda do dia', icon: CalendarToday },
                { path: '/saude/relatorios/ritmo-visitas-acs', label: 'Ritmo de Visitas do ACS', icon: DirectionsWalk },
                { path: '/saude/relatorios/resumo-executivo', label: 'Resumo Executivo', icon: Assessment },
                { path: '/saude/relatorios/equipes-sem-producao', label: 'Equipes sem Producao', icon: GroupRemove },
                { path: '/saude/relatorios/fichas-rascunho', label: 'Fichas em Rascunho', icon: EditNote },
                { path: '/saude/relatorios/profissionais-duplicados', label: 'Profissionais Duplicados', icon: PersonSearch },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {/* Paineis de Eficiencia Section */}
        {(hasPermission('VIEW_PAINEIS_EFICIENCIA') || hasPermission('VIEW_PAINEIS_FILAS_ESPERA') || hasPermission('VIEW_PAINEIS_FILAS_CONSULTORIOS') || hasPermission('VIEW_PAINEIS_INDICE_PRESCRICOES') || hasPermission('VIEW_PAINEIS_OCUPACAO_LEITOS') || hasPermission('VIEW_PAINEIS_PRODUTIVIDADE') || hasPermission('VIEW_PAINEIS_PESQUISA_SATISFACAO') || hasPermission('VIEW_PAINEIS_PRIMEIROS_ATENDIMENTOS') || hasPermission('VIEW_PAINEIS_PRODUCAO') || hasPermission('VIEW_PAINEIS_ESTATISTICAS_INTERNACAO') || hasPermission('VIEW_PAINEIS_TEMPO_REGULACAO')) && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Painéis de Eficiência
            </Typography>
            <List disablePadding>
              {[
                { path: '/saude/atendimentos-24h', label: 'Atendimentos 24h', icon: AccessTimeIcon, perm: 'VIEW_PAINEIS_EFICIENCIA' },
                { path: '/saude/filas-espera', label: 'Filas e Espera', icon: HourglassBottomIcon, perm: 'VIEW_PAINEIS_FILAS_ESPERA' },
                { path: '/saude/filas-consultorios', label: 'Filas de Consultórios', icon: MeetingRoomIcon, perm: 'VIEW_PAINEIS_FILAS_CONSULTORIOS' },
                { path: '/saude/indice-prescricoes', label: 'Índice de Prescrições', icon: ReceiptLongIcon, perm: 'VIEW_PAINEIS_INDICE_PRESCRICOES' },
                { path: '/saude/ocupacao-leitos', label: 'Ocupação de Leitos', icon: KingBedIcon, perm: 'VIEW_PAINEIS_OCUPACAO_LEITOS' },
                { path: '/saude/produtividade', label: 'Produtividade', icon: TrendingUp, perm: 'VIEW_PAINEIS_PRODUTIVIDADE' },
                { path: '/saude/pesquisa-satisfacao', label: 'Pesquisas Satisfação', icon: SentimentSatisfiedAltIcon, perm: 'VIEW_PAINEIS_PESQUISA_SATISFACAO' },
                { path: '/saude/primeiros-atendimentos', label: 'Primeiros Atendimentos', icon: PersonAddAlt1Icon, perm: 'VIEW_PAINEIS_PRIMEIROS_ATENDIMENTOS' },
                { path: '/saude/producao-ambulatorial', label: 'Produção', icon: PaidIcon, perm: 'VIEW_PAINEIS_PRODUCAO' },
                { path: '/saude/estatisticas-internacao', label: 'Estatísticas de Internação', icon: AnalyticsIcon, perm: 'VIEW_PAINEIS_ESTATISTICAS_INTERNACAO' },
                { path: '/saude/tempo-regulacao', label: 'Tempo de Regulação', icon: AssignmentTurnedInIcon, perm: 'VIEW_PAINEIS_TEMPO_REGULACAO' },
              ].filter((item) => hasPermission(item.perm)).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {/* Monitoramento Section */}
        {hasPermission('VIEW_MONITORAMENTO') && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Monitoramento
            </Typography>
            <List disablePadding>
              {[
                { path: '/monitoramento', label: 'Visão Geral', icon: MonitorHeartIcon },
                { path: '/monitoramento/leitos', label: 'Leitos', icon: HotelIcon },
                { path: '/monitoramento/internacoes', label: 'Internacoes', icon: MedicalServicesIcon },
                { path: '/monitoramento/tempos-espera', label: 'Tempos de Espera', icon: TimerIcon },
                { path: '/monitoramento/regulacao', label: 'Regulacao', icon: Assessment },
                { path: '/monitoramento/fila-espera', label: 'Fila de Espera', icon: ListIcon },
                { path: '/monitoramento/dispensacoes', label: 'Farmacia', icon: MedicationIcon },
                { path: '/monitoramento/cid', label: 'CID / Diagnosticos', icon: BiotechIcon },
                { path: '/monitoramento/geo', label: 'Geolocalizacao', icon: Map },
                { path: '/monitoramento/pacientes', label: 'Pacientes', icon: PeopleIcon },
                { path: '/monitoramento/suspensao', label: 'Suspensao Incentivos', icon: WarningIcon },
                { path: '/monitoramento/icsaps', label: 'ICSAPS', icon: LocalHospital },
                { path: '/monitoramento/vacinas', label: 'Vacinacao', icon: VaccinesIcon },
                { path: '/monitoramento/dcnt', label: 'DCNT', icon: Favorite },
                { path: '/monitoramento/parametros', label: 'Parametros', icon: BarChart },
                { path: '/monitoramento/visa', label: 'Vigilancia Sanitaria', icon: ShieldIcon },
                { path: '/monitoramento/visa-geo', label: 'VISA Georreferenciamento', icon: LocationOn },
                { path: '/monitoramento/visa-web', label: 'VISA Monitoramento Web', icon: Language },
                { path: '/monitoramento/visa-web/detalhes', label: 'VISA Documentos Web', icon: Description },
                { path: '/monitoramento/vigilancia-epidemiologica', label: 'Vigilancia Epidemiologica', icon: CoronavirusIcon },
                { path: '/monitoramento/vigilancia-epidemiologica/detalhes', label: 'Agravos / Registros', icon: Description },
                { path: '/monitoramento/mac', label: 'Gestao MAC', icon: AttachMoneyIcon },
                { path: '/monitoramento/equipamentos', label: 'Equipamentos', icon: Build },
                { path: '/monitoramento/fpo', label: 'Auditoria FPO', icon: FactCheckIcon },
                { path: '/monitoramento/repasses', label: 'Repasses FNS', icon: Public },
                { path: '/monitoramento/unidades-moveis', label: 'Unidades Moveis', icon: LocalShipping },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}


        {/* Atenção Especializada Section */}
        {hasPermission('VIEW_REPORTS') && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Atenção Especializada
            </Typography>
            <List disablePadding>
              {[
                { path: '/especializada', label: 'Visão Geral', icon: MedicalServicesIcon },
                { path: '/especializada/rede', label: 'Rede Especializada', icon: Map },
                { path: '/especializada/prospeccao', label: 'Prospecção', icon: TrendingUp },
                { path: '/especializada/producao', label: 'Produção', icon: Assessment },
                { path: '/especializada/custos', label: 'Custos', icon: AttachMoneyIcon },
                { path: '/especializada/predicao', label: 'Predição IA', icon: Psychology },
                { path: '/especializada/planejamento', label: 'Planejamento', icon: AccountTree },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}


        {/* Gestao Municipal Section */}
        {hasPermission('VIEW_REPORTS') && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Gestao Municipal
            </Typography>
            <List disablePadding>
              {[
                { path: '/iegm', label: 'IEGM Geral', icon: Assessment },
                { path: '/iegm/isaude', label: 'I-Saude', icon: LocalHospital },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
        {/* Admin Section */}
        {hasPermission('MANAGE_USERS') && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={navSectionSx}>
              Administracao
            </Typography>
            <List disablePadding>
              {[
                { path: '/usuarios', label: 'Usuarios', icon: People },
                { path: '/menus', label: 'Menus', icon: ViewList },
                { path: '/municipios', label: 'Municipios', icon: LocationCity },
                { path: '/perfis', label: 'Perfis', icon: Settings },
                ...(hasPermission('MANAGE_SETTINGS') || user?.is_admin
                  ? [{ path: '/configuracoes', label: 'Configuracoes', icon: Build }]
                  : []),
              ].map((item) => {
                const Icon = item.icon;
                // Subtelas (ex.: /municipios/12) mantem o item do cadastro destacado.
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                      sx={{
                        ...navItemSx(isActive),
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon sx={{ fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 600 : 500, color: 'inherit' }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ flexShrink: 0, borderTop: `1px solid ${SB.border}`, px: 3, py: 1.5 }}>
        <Typography sx={{ fontSize: '0.6875rem', color: SB.textMuted }}>
          {branding.branding_nome || 'NeoGestor'} &copy; {new Date().getFullYear()}
        </Typography>
      </Box>
    </Box>
  );

  // ─── BREADCRUMB ───
  const Breadcrumb = () => {
    if (location.pathname === '/' || isDashboardPage) return null;

    const segments = location.pathname.split('/').filter(Boolean);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2, fontSize: '0.875rem', flexWrap: 'wrap' }}>
        <Home
          sx={{ fontSize: 16, color: crescer.textFaint, cursor: 'pointer', '&:hover': { color: crescer.text }, transition: 'color 0.15s' }}
          onClick={() => navigate('/')}
        />
        {segments.map((segment, index) => {
          const isLast = index === segments.length - 1;
          const label = (segments[index - 1] === 'municipios' && /^\d+$/.test(segment))
            ? 'Editar município'
            : segmentLabels[segment] || (segment.charAt(0).toUpperCase() + segment.slice(1));
          const path = '/' + segments.slice(0, index + 1).join('/');

          return (
            <React.Fragment key={path}>
              <ChevronRight sx={{ fontSize: 14, color: crescer.border }} />
              {isLast ? (
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: crescer.text }}>
                  {label}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: crescer.textFaint,
                    cursor: 'pointer',
                    '&:hover': { color: crescer.text },
                    transition: 'color 0.15s',
                  }}
                  onClick={() => navigate(path)}
                >
                  {label}
                </Typography>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  // ─── TOPBAR ───
  const TopBar = () => (
    <Box
      component="header"
      sx={{
        // Altura base 64px + área segura do topo (notch). Em web/desktop env() = 0.
        height: 'calc(64px + env(safe-area-inset-top, 0px))',
        pt: 'env(safe-area-inset-top, 0px)',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: crescer.surface,
        px: { xs: 2, lg: 3 },
      }}
    >
      {/* Left */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Hamburger mobile */}
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{ display: { xs: 'flex', lg: 'none' }, color: crescer.textFaint, '&:hover': { backgroundColor: crescer.surface2 } }}
        >
          <MenuIcon sx={{ fontSize: 22 }} />
        </IconButton>
        {/* Botão Home em dashboards */}
        {isDashboardPage && (
          <IconButton
            onClick={() => navigate('/')}
            sx={{ color: crescer.textFaint, '&:hover': { backgroundColor: crescer.surface2 } }}
          >
            <Home sx={{ fontSize: 22 }} />
          </IconButton>
        )}
      </Box>

      {/* Right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {/* Alertas */}
        <AlertaBadge />

        {/* User Dropdown */}
        <Box
          onClick={(e) => setProfileAnchor(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            borderRadius: '8px',
            px: 1,
            py: 0.5,
            '&:hover': { backgroundColor: crescer.surface2 },
            transition: 'background-color 0.15s',
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: crescer.aiGrad,
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'white',
            }}
          >
            {user?.nome ? `${user.nome.charAt(0)}${user.nome.split(' ')[1]?.charAt(0) || ''}`.toUpperCase() : 'U'}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: crescer.text, lineHeight: 1.3 }}>
              {user?.nome || 'Usuario'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: crescer.textFaint, lineHeight: 1.3 }}>
              {user?.is_admin ? 'Administrador' : 'Usuario'}
            </Typography>
          </Box>
          <ExpandMore sx={{ fontSize: 16, color: crescer.textFaint, display: { xs: 'none', sm: 'block' } }} />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: crescer.bg }}>
      {/* Profile Menu (dropdown) */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 280,
            mt: 1,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: crescer.text }}>
            {user?.nome}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: crescer.textFaint }}>
            {user?.email}
          </Typography>
        </Box>
        <Box sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => { setProfileAnchor(null); }}
            sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: crescer.text, gap: 1.5 }}
          >
            <AccountCircle sx={{ fontSize: 18 }} /> Meu Perfil
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => { setProfileAnchor(null); handleLogout(); }}
            sx={{ py: 1.5, px: 2, fontSize: '0.875rem', color: crescer.danger, gap: 1.5 }}
          >
            <Logout sx={{ fontSize: 18 }} /> Sair
          </MenuItem>
        </Box>
      </Menu>

      {/* Município Menu */}
      <Menu
        anchorEl={municipioAnchor}
        open={Boolean(municipioAnchor)}
        onClose={() => setMunicipioAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            minWidth: 220,
            maxHeight: 400,
            mt: 0.5,
            borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgb(0 0 0 / 0.15)',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: crescer.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Trocar Municipio
          </Typography>
        </Box>
        {userMunicipios.map((mun) => (
          <MenuItem
            key={mun.id}
            onClick={() => handleSelectMunicipio(mun)}
            selected={municipioAtivo?.id === mun.id}
            sx={{
              py: 1.25,
              px: 2,
              '&.Mui-selected': {
                backgroundColor: 'rgba(0,122,240,0.08)',
                '&:hover': { backgroundColor: 'rgba(0,122,240,0.12)' },
              },
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: municipioAtivo?.id === mun.id ? 600 : 400, color: crescer.text }}>
                {mun.nome}
                {municipioAtivo?.id === mun.id && (
                  <Typography component="span" sx={{ ml: 1, fontSize: '0.6875rem', color: crescer.brandDark }}>(atual)</Typography>
                )}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Sidebar Nav */}
      <Box component="nav" sx={{ width: { lg: SIDEBAR_WIDTH }, flexShrink: { lg: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, borderRight: 'none' },
            '& .MuiBackdrop-root': { backgroundColor: 'rgba(0,0,0,0.5)' },
          }}
        >
          {sidebarContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              borderRight: 'none',
              height: '100vh',
              position: 'fixed',
              top: 0,
              left: 0,
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>
      </Box>

      {/* Main Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {/* TopBar */}
        {TopBar()}

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            ...(isDashboardPage
              ? { overflow: 'hidden', p: 0 }
              : { px: { xs: 2, lg: 3 }, pt: 2, pb: 'calc(16px + env(safe-area-inset-bottom, 0px))' }),
          }}
        >
          {!isDashboardPage && <Breadcrumb />}
          {/* key por unidade: ao trocar/limpar a Unidade de Saude, a pagina remonta e refaz o fetch */}
          <React.Fragment key={`unid-${unidadeSelecionada?.codunidade ?? 'todas'}`}>
            <Outlet />
          </React.Fragment>
        </Box>
      </Box>
    </Box>
  );
};

export default ModernLayout;
